import { Link } from "react-router-dom";
import { Search, Compass, Brain, ArrowRight } from "lucide-react";
import { SectionHeader } from "@/components/sections/SectionHeader";

const engagements = [
  {
    icon: Search,
    title: "Diagnostic & Assessment",
    tagline: "Every serious transformation starts with truth.",
    description: "We conduct structured diagnostics across operations & workflows, finance & compliance, and AI readiness & system maturity.",
    outcomes: [
      "Clear bottlenecks",
      "Risk exposure",
      "Opportunity prioritization",
    ],
    note: "Most long-term advisory engagements begin here.",
  },
  {
    icon: Compass,
    title: "Strategy & Advisory",
    tagline: "Clarity before acceleration.",
    description: "We help leadership teams redesign operating models, integrate AI responsibly and profitably, and improve cost efficiency & execution velocity.",
    outcomes: [
      "A usable roadmap, not a slide deck",
      "Decisions aligned to long-term value",
    ],
  },
  {
    icon: Brain,
    title: "Remote HQ & Fractional Advisory",
    tagline: "Big-firm thinking, embedded in your business.",
    description: "We serve as your on-demand C-Suite, providing high-level guidance without the full-time headcount.",
    outcomes: [
      "Continuous progress",
      "Leadership leverage",
      "Controlled, measurable evolution",
    ],
  },
];

export const WhatWeDoSection = () => {
  return (
    <section className="section-padding bg-background">
      <div className="section-container">
        <SectionHeader
          eyebrow="What We Do"
          title="Engagements, Not Services"
          subtitle="We don't sell hours. We deliver outcomes through structured, results-oriented engagements."
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {engagements.map((engagement, index) => (
            <div
              key={index}
              className="group border border-border p-8 transition-all duration-300 hover:border-primary/30 hover:shadow-lg"
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center bg-primary text-primary-foreground mb-6">
                <engagement.icon size={28} />
              </div>

              {/* Content */}
              <h3 className="heading-4 mb-2">{engagement.title}</h3>
              <p className="font-serif text-base italic text-muted-foreground mb-4">
                {engagement.tagline}
              </p>
              <p className="body-base text-muted-foreground mb-6">
                {engagement.description}
              </p>

              {/* Outcomes */}
              <div className="pt-6 border-t border-border">
                <p className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Outcome
                </p>
                <ul className="space-y-2">
                  {engagement.outcomes.map((outcome, i) => (
                    <li key={i} className="font-sans text-sm text-foreground">
                      {outcome}
                    </li>
                  ))}
                </ul>
                {engagement.note && (
                  <p className="font-sans text-xs text-muted-foreground mt-4 italic">
                    {engagement.note}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/solutions"
            className="font-sans text-sm font-medium text-foreground hover:text-muted-foreground transition-colors inline-flex items-center gap-2"
          >
            Explore our full engagement model
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};
